import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { getAuth, onIdTokenChanged, signOut, Unsubscribe } from "firebase/auth";

@Injectable({
  providedIn: 'root'
})
export class FirebaseManagerService implements OnDestroy {
  private logoutTimeout: any;
  private unsubscribeAuth: Unsubscribe | null = null;
  private readonly _fuseConfirmationService = inject(FuseConfirmationService);


  constructor(
        private _router: Router
      ) {}

  public startTokenMonitor(): void {
    const auth = getAuth();
    
    this.unsubscribeAuth = onIdTokenChanged(auth, async (user) => {
      this.clearLogoutTimeout();

      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        const expirationTime = new Date(idTokenResult.expirationTime).getTime();
        const msRemaining = expirationTime - Date.now();
        
        if (msRemaining > 0) {
          this.logoutTimeout = setTimeout(() => {
            this.handleLogoutAndCleanup();
          }, msRemaining);
        } else {
          // Token is already expired on startup
          this._router.navigate(['/sign-in'], {
              queryParams: { reason: 'session-expired' },
          });
        }
      }
    });
  }

  private async handleLogoutAndCleanup(): Promise<void> {
    await this.executeSystemSignOut();

    const confirmationDialog = this._fuseConfirmationService.open({
        title: 'Session Timeout',
        message: 'Your session has ended. Please sign in again to continue.',
        icon: {
            show: true,
            name: 'heroicons_outline:exclamation-triangle',
            color: 'warning',
        },
        actions: {
            confirm: {
                show: true,
                label: 'Sign in again',
                color: 'primary',
            },
            cancel: {
                show: false,
            },
        },
        dismissible: false,
    });

    confirmationDialog.afterClosed().subscribe(() => {
        this._router.navigate(['/sign-in'], {
            queryParams: { reason: 'session-expired' },
        });
    });
  }
  
  private async executeSystemSignOut(): Promise<void> {
    const auth = getAuth();
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Firebase signOut failed:", error);
    } finally {
      localStorage.clear();
      sessionStorage.clear();
    }
  }

  private clearLogoutTimeout(): void {
    if (this.logoutTimeout) {
      clearTimeout(this.logoutTimeout);
      this.logoutTimeout = null;
    }
  }

  ngOnDestroy(): void {
    this.clearLogoutTimeout();
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  }
}